import React, { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Last-resort catcher for render-time errors anywhere below the root —
 * WASM init crash, missing storage API, malformed state coming back
 * from CloudStorage. Without this the user sees a white screen and
 * has no way out except force-closing the Mini App.
 *
 * Recovery: "Reload" reloads the page (TG WebView re-requests the
 * Mini App URL). "Wipe wallet & reload" clears local storage first,
 * giving the user an escape hatch if the keystore itself is corrupt.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Best-effort telemetry. console.error keeps it visible in
    // Telegram's web inspector when devs are debugging on-device.
    console.error("[ZcashWallet] render error:", error, info.componentStack);
  }

  private reload = () => {
    window.location.reload();
  };

  private wipeAndReload = () => {
    try {
      // CloudStorage we can't wipe synchronously without the WebApp
      // API — defer to a reload, IndexedDB clearing handles the
      // common "stuck on a corrupt key" case.
      indexedDB.deleteDatabase("teleswap-wallet");
      Object.keys(localStorage)
        .filter(
          (k) =>
            k.startsWith("teleswap:") ||
            k.startsWith("telecrypt.") ||
            k.startsWith("zcashwallet."),
        )
        .forEach((k) => localStorage.removeItem(k));
    } catch {
      // ignore, we're reloading anyway
    }
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "32px 24px",
          background: "var(--bg-1, #0E1116)",
          color: "var(--text, #E8EAED)",
          fontFamily: "var(--font-text, system-ui, sans-serif)",
        }}
      >
        <div style={{ fontSize: 38, marginBottom: 14 }}>⚠️</div>
        <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 8 }}>
          Something broke.
        </div>
        <div
          style={{
            fontSize: 13,
            color: "var(--hint, #8A9099)",
            textAlign: "center",
            maxWidth: 320,
            marginBottom: 18,
            lineHeight: 1.5,
          }}
        >
          {this.state.error.message || "Unexpected error."}
        </div>
        <button
          onClick={this.reload}
          style={{
            background: "var(--tg-theme-button-color, #2c8acb)",
            color: "var(--tg-theme-button-text-color, white)",
            border: "none",
            borderRadius: 10,
            padding: "12px 22px",
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
            marginBottom: 10,
            minWidth: 180,
          }}
        >
          Reload
        </button>
        <button
          onClick={this.wipeAndReload}
          style={{
            background: "transparent",
            color: "var(--danger, #ff6262)",
            border: "1px solid var(--danger, #ff6262)",
            borderRadius: 10,
            padding: "10px 20px",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            minWidth: 180,
          }}
        >
          Wipe wallet &amp; reload
        </button>
      </div>
    );
  }
}
